import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import GaugeComponent from 'react-gauge-component';
import { useTheme } from '@emotion/react';

// Debounce Hook
const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const MeterChartOffer = memo(
  ({ offerValue, priceEstimate, fromBuyerManageOffer, isFocusMode }) => {
    const theme = useTheme();
    const matchessm = useMediaQuery(theme.breakpoints.down('sm'));

    // Debounced value
    const debouncedOfferValue = useDebounce(offerValue, 500);

    // Gauge Logic
    const rawPercent = (debouncedOfferValue / priceEstimate) * 100;

    const percentValue = useMemo(() => {
      if (!debouncedOfferValue || !priceEstimate) return 0;

      if (rawPercent < 50) return 0;

      return Math.min((rawPercent - 50) * 2, 100);
    }, [debouncedOfferValue, priceEstimate, rawPercent]);

    const targetGaugePercent = percentValue / 100;

    const [animatedPercent, setAnimatedPercent] = useState(0);

    useEffect(() => {
      const timer = setInterval(() => {
        setAnimatedPercent((prev) => {
          const diff = targetGaugePercent - prev;

          if (Math.abs(diff) < 0.01) {
            return targetGaugePercent;
          }

          return prev + diff * 0.08;
        });
      }, 50); // 20fps

      return () => clearInterval(timer);
    }, [targetGaugePercent]);

    // Label
    const levelText = useMemo(() => {
      if (!priceEstimate || !debouncedOfferValue) return 'Low';

      if (rawPercent <= 60) return 'Low';
      if (rawPercent <= 70) return 'Moderate';
      if (rawPercent <= 80) return 'Competitive';
      if (rawPercent <= 90) return 'Strong';

      return 'Outstanding';
    }, [rawPercent, priceEstimate, debouncedOfferValue]);

    return (
      <Box
        sx={{
          display: matchessm ? 'none' : 'flex',
          flexDirection: fromBuyerManageOffer ? 'column' : 'row',
          alignItems: 'center',
          gap: fromBuyerManageOffer ? '5px' : '20px',
          justifyContent: isFocusMode ? 'start' : 'end',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontSize: '16px',
          }}
        >
          Your Offer Level
        </Typography>

        <Stack alignItems="center" justifyContent="center">
          <Box
            sx={{
              width: 150,
              height: 70,
            }}
          >
            <GaugeComponent
              value={animatedPercent * 100}
              type="semicircle"
              labels={{
                valueLabel: {
                  hide: true,
                },
                tickLabels: {
                  hideMinMax: true,
                },
              }}
              arc={{
                width: 0.3,
                padding: 0,
                subArcs: [
                  {
                    limit: 20,
                    color: '#ff0000',
                  },
                  {
                    limit: 40,
                    color: '#ff9900',
                  },
                  {
                    limit: 60,
                    color: '#ffcc00',
                  },
                  {
                    limit: 80,
                    color: '#99cc00',
                  },
                  {
                    limit: 100,
                    color: '#00cc66',
                  },
                ],
              }}
              pointer={{
                color: '#000',
                length: 0.75,
                width: 11,
                elastic: true,
                animate: true,
              }}
            />
          </Box>

          <Box
            sx={{
              position: 'relative',
              height: '24px',
              overflow: 'hidden',
              mt: 0.5,
            }}
          >
            <Typography
              key={levelText}
              className="text-slide-animation"
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 500,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                // pr: '25px',
              }}
            >
              {levelText}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  },
  (prev, next) =>
    prev.offerValue === next.offerValue &&
    prev.priceEstimate === next.priceEstimate
);

export default MeterChartOffer;
